import { useState, useRef, useMemo } from 'react';
import { Play, Pause, Loader2, AlertCircle, Flame } from 'lucide-react';

// Global audio reference to pause other audio when playing new one
let currentPlayingAudio = null;

const VoiceNotePlayer = ({ src, duration, isListenOnce, isMine, onPlay, onError }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(duration || 0);
  const [hasError, setHasError] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds) || seconds === Infinity) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Toggle playback speed
  const toggleSpeed = (e) => {
    e.stopPropagation();
    const speeds = [1, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    setPlaybackRate(nextSpeed);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextSpeed;
    }
  };

  // Handle play/pause
  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio || isLoading) return;

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        // Pause any other playing audio first
        if (currentPlayingAudio && currentPlayingAudio !== audio) {
          currentPlayingAudio.pause();
        }
        currentPlayingAudio = audio;
        setIsLoading(true);
        
        await audio.play();
        setIsPlaying(true);
        setIsLoading(false);
        if (onPlay) onPlay();
      }
    } catch (error) {
      console.error('Audio playback error:', error);
      setIsLoading(false);
      setHasError(true);
      if (onError) onError();
    }
  };

  // Handle seek on waveform click
  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !audioDuration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * audioDuration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Progress percentage
  const progress = audioDuration > 0 ? (currentTime / audioDuration) * 100 : 0;

  // Generate waveform bars
  const barCount = 32;
  const waveformBars = useMemo(() => {
    const bars = [];
    for (let i = 0; i < barCount; i++) {
      const height = 30 + Math.sin(i * 0.7) * 25 + Math.cos(i * 1.3) * 20 + Math.sin(i * 2.1) * 10;
      bars.push(Math.max(15, Math.min(100, height)));
    }
    return bars;
  }, []);

  if (hasError) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 rounded-2xl" style={{ background: 'rgba(239,68,68,0.15)' }}>
        <AlertCircle className="h-4 w-4 text-red-400" />
        <span className="text-xs text-red-400">Audio unavailable</span>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center gap-2 py-1.5 px-2 rounded-2xl transition-all"
      style={{ 
        background: isMine 
          ? 'linear-gradient(135deg, rgba(124,58,237,0.15) 0%, rgba(91,33,182,0.2) 100%)'
          : 'rgba(30,41,59,0.8)',
        minWidth: '200px',
        maxWidth: '280px',
        boxShadow: isPlaying ? (isMine ? '0 0 12px rgba(124,58,237,0.3)' : '0 0 12px rgba(59,130,246,0.2)') : 'none'
      }}
    >
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={(e) => {
          setAudioDuration(e.target.duration);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
          currentPlayingAudio = null;
        }}
        onError={() => {
          setHasError(true);
          setIsLoading(false);
          if (onError) onError();
        }}
        onPlay={() => { setIsPlaying(true); setIsLoading(false); }}
        onPause={() => setIsPlaying(false)}
        onWaiting={() => setIsLoading(true)}
        onCanPlay={() => setIsLoading(false)}
      />
      
      {/* LEFT: Play/Pause button */}
      <button
        onClick={togglePlay}
        disabled={isLoading}
        className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-all active:scale-90"
        style={{ 
          background: isMine 
            ? 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' 
            : 'linear-gradient(135deg, #475569 0%, #334155 100%)',
        }}
        data-testid="voice-note-play-btn"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 text-white animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-4 w-4 text-white" fill="white" />
        ) : (
          <Play className="h-4 w-4 text-white ml-0.5" fill="white" />
        )}
      </button>

      {/* CENTER: Waveform */}
      <div 
        className="flex-1 flex items-center gap-px h-8 cursor-pointer px-1"
        onClick={handleSeek}
      >
        {waveformBars.map((height, i) => {
          const barProgress = (i / barCount) * 100;
          const isActive = barProgress <= progress;
          const isAnimating = isPlaying && Math.abs(barProgress - progress) < 10;
          
          return (
            <div
              key={i}
              className="w-[3px] rounded-full transition-all"
              style={{
                height: `${height}%`,
                minHeight: '3px',
                background: isActive 
                  ? (isMine ? '#A78BFA' : '#94A3B8')
                  : 'rgba(255,255,255,0.15)',
                transform: isAnimating ? `scaleY(${1 + Math.sin(Date.now() / 100 + i) * 0.2})` : 'scaleY(1)',
                transition: isAnimating ? 'none' : 'all 0.1s ease',
              }}
            />
          );
        })}
      </div>

      {/* RIGHT: Duration + Speed */}
      <div className="flex flex-col items-end shrink-0 gap-0.5">
        <span className="text-[11px] font-medium tabular-nums" style={{ color: isMine ? '#C4B5FD' : '#94A3B8' }}>
          {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(audioDuration)}
        </span>
        <button
          onClick={toggleSpeed}
          className="text-[10px] px-1 rounded transition-colors"
          style={{ 
            color: isMine ? '#A78BFA' : '#64748B',
            background: playbackRate !== 1 ? 'rgba(255,255,255,0.1)' : 'transparent'
          }}
        >
          {playbackRate}x
        </button>
      </div>

      {/* Listen once indicator */}
      {isListenOnce && !isMine && (
        <Flame className="h-3 w-3 text-orange-400 shrink-0" />
      )}
    </div>
  );
};

export default VoiceNotePlayer;
